/**
 * O formulário do agendamento (fase 2 da agenda da clínica): os campos do
 * "Horário Vago" do SISAC, o que é obrigatório, a idade e os parâmetros que o
 * `agendar` leva. Sem React, para o teste não precisar de tela.
 *
 * ── Obrigatórios, como a recepção da clínica pediu (24/10) ───────────────
 *
 * Especialidade e médico (o passo 1), a vaga (o passo 2), e aqui: local,
 * origem, paciente, nascimento, CEP, endereço (com número, bairro, cidade e
 * UF, desde 03/11), convênio, plano e tipo. O CPF não é
 * obrigatório; o paciente pode ir sem cadastro no SISAC, só pelo nome.
 *
 * ── O código, nunca o nome ───────────────────────────────────────────────
 *
 * Convênio, plano, local e origem vão pelo código que a lista do SISAC deu.
 * A tela mostra o nome; o SISAC grava o código.
 *
 * ── Os padrões são da organização (#1187) ────────────────────────────────
 *
 * Local, origem, convênio e plano começam no que a organização gravou na tela
 * "Agenda da clínica" (o `/v1/me` os traz em `clinica_padroes`). O convênio e
 * o plano do cadastro do paciente vinculado vencem os padrões. O tipo começa
 * sempre em "Consulta": retorno ou exame é escolha de cada agendamento.
 *
 * ── Endereço e documento vão só para a agenda (#1187) ────────────────────
 *
 * Endereço, número, bairro, cidade e UF são obrigatórios; RG e órgão emissor,
 * opcionais. Todos vão para as colunas da agenda do SISAC; o cadastro do paciente e a ficha do
 * CRM não mudam.
 */

import type {
  AgendamentoDaConsulta,
  DadosDoPacienteParaAgendar,
  PadroesDaAgenda,
} from '@cliqcrm/contracts';

/** Uma opção de lista do SISAC (convênio, plano, local, origem). */
export type OpcaoDaLista = { readonly valor: string; readonly rotulo: string };

/** O que o "Tipo" do SISAC aceita: a finalidade da vaga. */
export const TIPOS_DE_AGENDAMENTO: readonly OpcaoDaLista[] = [
  { valor: 'CONSULTA', rotulo: 'Consulta' },
  { valor: 'RETORNO', rotulo: 'Retorno' },
  { valor: 'EXAME', rotulo: 'Exame' },
];

export const SEXOS: readonly OpcaoDaLista[] = [
  { valor: 'F', rotulo: 'Feminino' },
  { valor: 'M', rotulo: 'Masculino' },
];

export interface FormularioDoAgendamento {
  /** Sem cadastro no SISAC: o paciente vai só pelo nome digitado. */
  readonly semCadastro: boolean;
  readonly nome: string;
  /** `AAAA-MM-DD`. */
  readonly nascimento: string;
  readonly sexo: string;
  readonly cpf: string;
  readonly cep: string;
  readonly endereco: string;
  readonly numero: string;
  readonly bairro: string;
  readonly cidade: string;
  /** A sigla, "MA". */
  readonly uf: string;
  readonly rg: string;
  readonly orgaoEmissor: string;
  readonly convenio: string;
  readonly plano: string;
  readonly matricula: string;
  readonly tipo: string;
  readonly local: string;
  readonly origem: string;
  readonly celular: string;
  readonly whatsapp: string;
  readonly email: string;
  readonly telemedicina: boolean;
  readonly urgente: boolean;
  readonly observacao: string;
}

export type CampoDoFormulario = keyof FormularioDoAgendamento;

/** Os tamanhos das colunas da agenda do SISAC: o que passa disso o banco recusaria. */
export const TAMANHOS_DO_ENDERECO = {
  endereco: 100,
  numero: 20,
  bairro: 50,
  cidade: 30,
  uf: 2,
  rg: 15,
  orgaoEmissor: 20,
} as const;

/** O formulário de partida: o cadastro do vinculado, e os padrões da organização. */
export function formularioInicial({
  dados,
  padroes = {},
  telefoneDoContato = '',
}: {
  dados: DadosDoPacienteParaAgendar | null;
  /** Os padrões da organização (`clinica_padroes` do `/v1/me`). */
  padroes?: PadroesDaAgenda;
  /**
   * O telefone do lead, já como o SISAC guarda (DDD e número). O WhatsApp é o
   * dele: é por onde a conversa acontece. O celular é o do cadastro, quando
   * há, e senão o mesmo.
   */
  telefoneDoContato?: string;
}): FormularioDoAgendamento {
  return {
    semCadastro: false,
    nome: '',
    nascimento: dados?.nascimento ?? '',
    sexo: (dados?.sexo ?? '').toUpperCase().slice(0, 1),
    cpf: '',
    cep: mascararCep(dados?.cep ?? ''),
    endereco: maiusculas(dados?.endereco ?? ''),
    numero: maiusculas(dados?.numero ?? ''),
    bairro: maiusculas(dados?.bairro ?? ''),
    cidade: maiusculas(dados?.cidade ?? ''),
    uf: maiusculas(dados?.uf ?? '').slice(0, 2),
    rg: maiusculas(dados?.rg ?? ''),
    orgaoEmissor: maiusculas(dados?.orgao_emissor ?? ''),
    /*
     * O convênio do cadastro vem com o plano dele; sem convênio no cadastro, os
     * padrões. Escondido, o convênio é sempre o padrão: o cadastro não o troca.
     */
    ...(dados?.cod_convenio && !convenioFixo(padroes)
      ? { convenio: dados.cod_convenio, plano: dados.plano ?? '' }
      : { convenio: padroes.convenio ?? '', plano: padroes.plano ?? '' }),
    matricula: maiusculas(dados?.matricula ?? ''),
    tipo: 'CONSULTA',
    local: padroes.local ?? '',
    origem: padroes.origem ?? '',
    celular: dados?.celular || telefoneDoContato,
    whatsapp: telefoneDoContato || (dados?.whatsapp ?? ''),
    email: dados?.email ?? '',
    telemedicina: false,
    urgente: false,
    observacao: '',
  };
}

/**
 * O formulário do "Editar" (#1187): o de partida, com o que está gravado na
 * vaga por cima. O que a vaga não tem fica como o de partida.
 */
export function formularioDoAgendamentoLido(
  base: FormularioDoAgendamento,
  dados: AgendamentoDaConsulta['dados'],
): FormularioDoAgendamento {
  if (!dados) return base;
  const texto = (valor: string | null, atual: string) => (valor ? maiusculas(valor) : atual);
  const marcado = (valor: string | null, atual: boolean) =>
    valor === null ? atual : valor === '1';
  return {
    ...base,
    nascimento: dados.data_nasc ?? base.nascimento,
    sexo: dados.sexo ? dados.sexo.toUpperCase().slice(0, 1) : base.sexo,
    cep: dados.cep ? mascararCep(dados.cep) : base.cep,
    convenio: dados.cod_convenio ?? base.convenio,
    plano: dados.plano ?? base.plano,
    matricula: texto(dados.matricula, base.matricula),
    tipo: dados.tipo ? dados.tipo.toUpperCase() : base.tipo,
    local: dados.local ?? base.local,
    origem: dados.origem ?? base.origem,
    celular: dados.celular ?? base.celular,
    whatsapp: dados.whatsapp ?? base.whatsapp,
    email: dados.email ?? base.email,
    telemedicina: marcado(dados.telemedicina, base.telemedicina),
    urgente: marcado(dados.urgente, base.urgente),
    observacao: texto(dados.observacao, base.observacao),
    endereco: texto(dados.endereco, base.endereco),
    numero: texto(dados.numero, base.numero),
    bairro: texto(dados.bairro, base.bairro),
    cidade: texto(dados.cidade, base.cidade),
    uf: dados.uf ? maiusculas(dados.uf).slice(0, 2) : base.uf,
    rg: texto(dados.rg, base.rg),
    orgaoEmissor: texto(dados.orgao_emissor, base.orgaoEmissor),
  };
}

/** Em maiúsculas, como o SISAC guarda os textos do cadastro e da agenda (#1187). */
export function maiusculas(texto: string): string {
  return texto.toLocaleUpperCase('pt-BR');
}

/** O convênio está escondido no formulário (#1187): vale sempre o padrão. */
export function convenioFixo(padroes: PadroesDaAgenda): boolean {
  return (padroes.ocultos ?? []).includes('convenio') && (padroes.convenio ?? '') !== '';
}

/** "12345-678". */
export function mascararCep(texto: string): string {
  const d = texto.replace(/\D/g, '').slice(0, 8);
  return d.length <= 5 ? d : `${d.slice(0, 5)}-${d.slice(5)}`;
}

/**
 * A idade como o SISAC a mostra: "28 anos e 8 meses", ou só os anos quando o
 * mês fecha. `null` sem nascimento válido.
 */
export function idadeEmPalavras(nascimento: string, hoje: Date): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(nascimento);
  if (!m) return null;
  const [ano, mes, dia] = [Number(m[1]), Number(m[2]), Number(m[3])];
  let meses = (hoje.getFullYear() - ano) * 12 + (hoje.getMonth() + 1 - mes);
  if (hoje.getDate() < dia) meses -= 1;
  if (meses < 0) return null;
  const anos = Math.floor(meses / 12);
  const resto = meses % 12;
  const textoDosAnos = anos === 1 ? '1 ano' : `${anos} anos`;
  if (resto === 0) return textoDosAnos;
  const textoDosMeses = resto === 1 ? '1 mês' : `${resto} meses`;
  return anos === 0 ? textoDosMeses : `${textoDosAnos} e ${textoDosMeses}`;
}

/**
 * O que falta para agendar, campo a campo, com o texto que a pessoa lê. Vazio
 * quando dá para agendar.
 */
export function errosDoFormulario(
  formulario: FormularioDoAgendamento,
  { temPaciente, hoje }: { temPaciente: boolean; hoje: Date },
): Partial<Record<CampoDoFormulario | 'paciente', string>> {
  const erros: Partial<Record<CampoDoFormulario | 'paciente', string>> = {};
  if (formulario.semCadastro) {
    if (formulario.nome.trim().split(/\s+/).filter(Boolean).length < 2) {
      erros.nome = 'Digite o nome completo do paciente.';
    }
  } else if (!temPaciente) {
    erros.paciente = 'Escolha o paciente do SISAC, ou agende sem cadastro.';
  }
  if (!formulario.nascimento) erros.nascimento = 'Informe a data de nascimento.';
  else if (idadeEmPalavras(formulario.nascimento, hoje) === null) {
    erros.nascimento = 'A data de nascimento não pode ser no futuro.';
  }
  if (formulario.cep.replace(/\D/g, '').length !== 8) erros.cep = 'O CEP tem 8 dígitos.';
  const telefone = (valor: string) => [10, 11].includes(valor.replace(/\D/g, '').length);
  if (!telefone(formulario.whatsapp)) erros.whatsapp = 'O WhatsApp com DDD.';
  if (!telefone(formulario.celular)) erros.celular = 'O celular com DDD.';
  if (!formulario.convenio) erros.convenio = 'Escolha o convênio.';
  if (!formulario.plano) erros.plano = 'Escolha o plano.';
  if (!formulario.tipo) erros.tipo = 'Escolha o tipo.';
  if (!formulario.local) erros.local = 'Escolha o local.';
  if (!formulario.origem) erros.origem = 'Escolha a origem.';
  const cpf = formulario.cpf.replace(/\D/g, '');
  if (cpf !== '' && cpf.length !== 11) erros.cpf = 'O CPF tem 11 dígitos, ou fica em branco.';
  // O endereço é obrigatório (#1187): o CEP o preenche, e o que faltar se digita.
  if (!formulario.endereco.trim()) erros.endereco = 'Informe o endereço.';
  if (!formulario.numero.trim()) erros.numero = 'Informe o número (ou SN).';
  if (!formulario.bairro.trim()) erros.bairro = 'Informe o bairro.';
  if (!formulario.cidade.trim()) erros.cidade = 'Informe a cidade.';
  if (!/^[A-Za-z]{2}$/.test(formulario.uf.trim())) erros.uf = 'A UF tem duas letras.';
  if (
    formulario.email.trim() !== '' &&
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formulario.email.trim())
  ) {
    erros.email = 'Confira o e-mail.';
  }
  return erros;
}

/**
 * Os parâmetros do `agendar`, pelos nomes do contrato. Vazio não vai: a
 * operação recebe nulo no que ela declara e a tela não preencheu.
 *
 * O nome e o CPF só vão sem cadastro: com o paciente vinculado, a operação os
 * copia do cadastro dele pelo código, e o servidor recusa os digitados. Sem
 * isso, um CPF digitado antes de vincular iria junto com outro paciente.
 */
export function parametrosDoAgendamento(
  formulario: FormularioDoAgendamento,
  {
    idAgenda,
    codigoDoPaciente,
    codigoDoMedico,
    codigoDaEspecialidade,
  }: {
    idAgenda: string;
    codigoDoPaciente: string | null;
    codigoDoMedico: string;
    codigoDaEspecialidade: string;
  },
): Record<string, string> {
  const valores: Record<string, string> = {
    id_agenda: idAgenda,
    cod_paciente: codigoDoPaciente ?? '',
    nome: formulario.semCadastro ? maiusculas(formulario.nome.trim().replace(/\s+/g, ' ')) : '',
    data_nasc: formulario.nascimento,
    sexo: formulario.sexo,
    cpf: formulario.semCadastro ? formulario.cpf.replace(/\D/g, '') : '',
    cep: formulario.cep.replace(/\D/g, ''),
    // Os textos vão em maiúsculas, como o SISAC guarda; o e-mail, não.
    endereco: maiusculas(formulario.endereco.trim().replace(/\s+/g, ' ')),
    numero: maiusculas(formulario.numero.trim()),
    bairro: maiusculas(formulario.bairro.trim().replace(/\s+/g, ' ')),
    cidade: maiusculas(formulario.cidade.trim().replace(/\s+/g, ' ')),
    uf: maiusculas(formulario.uf.trim()),
    rg: maiusculas(formulario.rg.trim()),
    orgao_emissor: maiusculas(formulario.orgaoEmissor.trim()),
    cod_convenio: formulario.convenio,
    plano: formulario.plano,
    matricula: maiusculas(formulario.matricula.trim()),
    tipo: formulario.tipo,
    cod_especialidade: codigoDaEspecialidade,
    cod_medico: codigoDoMedico,
    local: formulario.local,
    origem: formulario.origem,
    celular: formulario.celular.replace(/\D/g, ''),
    whatsapp: formulario.whatsapp.replace(/\D/g, ''),
    email: formulario.email.trim(),
    telemedicina: formulario.telemedicina ? '1' : '0',
    urgente: formulario.urgente ? '1' : '0',
    observacao: maiusculas(formulario.observacao.trim()),
  };
  return Object.fromEntries(Object.entries(valores).filter(([, valor]) => valor !== ''));
}

/**
 * As linhas de uma lista do SISAC como opções. O servidor já as entrega como
 * `{ codigo, nome }` (ele escolhe a coluna certa de cada lista): aqui só se tira
 * o que vier sem um dos dois.
 */
export function opcoesDaLista(
  linhas: readonly Readonly<Record<string, unknown>>[],
): OpcaoDaLista[] {
  return linhas.flatMap((linha) => {
    const valor = typeof linha['codigo'] === 'string' ? linha['codigo'].trim() : '';
    const rotulo = typeof linha['nome'] === 'string' ? linha['nome'].trim() : '';
    return valor && rotulo ? [{ valor, rotulo }] : [];
  });
}

/** A opção cujo nome contém o texto (sem caixa nem acento), para os padrões da recepção. */
export function opcaoPeloNome(opcoes: readonly OpcaoDaLista[], nome: string): string {
  const limpo = (t: string) => t.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
  return opcoes.find((opcao) => limpo(opcao.rotulo).includes(limpo(nome)))?.valor ?? '';
}
